import { getCustomersAPI } from '@/src/apis/rest/customers';
import { GetCustomerByPartnerLinkIdRequestParams } from '@/src/apis/rest/partnershipCompaines.dtos';
import {
  getCustomerByPartnerLinkIdAPI,
  getCustomerByPartnerLinkIdExcelAPI,
  getPartnershipCustomersExcelAPI,
  getRecyclingSummaryAPI,
  getRecyclingSummaryExcelAPI,
} from '@/src/apis/rest/partnershipCompanies';
import Button from '@/src/components/atom/Button';
import DateRangePicker from '@/src/components/atom/DateRangePicker';
import Dialog from '@/src/components/atom/Dialog';
import ExcelDownloadButton from '@/src/components/atom/ExcelDownloadButton';
import Input from '@/src/components/atom/Input';
import Table from '@/src/components/atom/Table';
import FormContent from '@/src/components/molecule/FormContent';
import SectionCard from '@/src/components/molecule/SectionCard';
import DetailSection from '@/src/components/organism/DetailSection';
import { DetailSctionRenderer } from '@/src/components/organism/DetailSection/renderer';
import { Form, FormField } from '@/src/components/ui/form';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { UNIT_STRINGS } from '@/src/constants/unit';
import { FormValues } from '@/src/pages/Challenge/field';
import { PartnerParticipantListColumn } from '@/src/pages/PartnershipLinks/column';
import { useQuery } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { useForm } from 'react-hook-form';

interface Props {
  isOpen: boolean;
  onCloseModal: () => void;
  partnerLinkId: number;
  partnerCode: string;
}

interface FromValues {
  createdAt: DateRange;
  createdAtFrom: string;
  createdAtTo: string;
  nickLike: string;
}

const PartnerParticipantsDetail = ({
  isOpen,
  onCloseModal,
  partnerLinkId,
  partnerCode,
}: Props) => {
  // state
  const gridRef = useRef<AgGridReact>(null);
  const [searchValues, setSearchValues] = useState<
    Partial<GetCustomerByPartnerLinkIdRequestParams>
  >({});

  // form
  const form = useForm<FromValues>({
    defaultValues: {
      createdAt: {
        from: undefined,
        to: undefined,
      },
      createdAtFrom: '',
      createdAtTo: '',
      nickLike: '',
    },
  });

  // query
  const { data: customerListData } = useQuery({
    queryKey: [
      'partnership-customer',
      partnerLinkId,
      searchValues.createdAtFrom,
      searchValues.createdAtTo,
      searchValues.nickLike,
    ],
    queryFn: () => {
      return getCustomerByPartnerLinkIdAPI({
        ...searchValues,
        signupSourceId: partnerLinkId,
      });
    },
    gcTime: 0,
    staleTime: 0,
  });
  const { data: recyclingSummaryData } = useQuery({
    queryKey: ['recycling-summary', partnerLinkId],
    queryFn: () => getRecyclingSummaryAPI({ id: partnerLinkId }),
    enabled: !!partnerLinkId,
  });

  // event
  const handleSubmit = (values: FromValues) => {
    const submitValues = { ...values };

    setSearchValues({
      createdAtFrom: submitValues.createdAtFrom,
      createdAtTo: submitValues.createdAtTo,
      nickLike: submitValues.nickLike,
    });
  };

  // render
  const getDateDisabledMatcher = (date: Date) => {
    if (dayjs().isAfter(date, 'D')) return true;

    return false;
  };
  const renderFooter = () => {
    return (
      <div className="flex justify-end gap-2">
        <Button
          buttonColor="outline"
          onClick={onCloseModal}
          type="button"
          className="w-[162px]"
        >
          {BUTTON_CONST.close}
        </Button>
      </div>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={onCloseModal}
      contentProps={{
        style: {
          width: 'calc(100vw - 24px)',
        },
        className: 'max-w-[1200px]',
      }}
      title="참여 정보 자세히 보기"
      className="h-[1000px]"
    >
      <div className="flex w-full flex-col gap-2">
        <DetailSection
          sectionTitle={
            <div className="flex w-full items-center justify-between">
              <span className="font-pretendardSemiBold text-b18sb">
                수거 신청 누적 정보
              </span>
              <ExcelDownloadButton
                downloadExcelAPI={getRecyclingSummaryExcelAPI}
                fileName={`${partnerCode}_수거신청_리스트`}
                searchParams={{ id: partnerLinkId }}
                buttonSize="small"
                buttonText="수거 신청 명단 다운로드"
                disabled={!recyclingSummaryData?.boxCount}
              />
            </div>
          }
          className="bg-inherit p-0 !shadow-none"
          data={recyclingSummaryData}
          contents={[
            [
              {
                key: 'boxCount',
                lable: '박스(봉투) 수',
                valueSetter: data => {
                  return `${DetailSctionRenderer.NUMBER()(data?.boxCount || 0)}개`;
                },
              },

              {
                key: 'totalWeight',
                lable: '자원순환량(Kg)',
                render: DetailSctionRenderer.NUMBER(),
              },

              {
                key: 'totalCarbonEmissionReduction',
                lable: '탄소 감축량(Kg)',
                render: DetailSctionRenderer.NUMBER(),
              },
            ],
            [
              {
                key: 'totalPineScore',
                lable: '소나무 지수',
                render: DetailSctionRenderer.NUMBER(),
              },
              {
                key: 'expectedCredit',
                lable: '지급 크레딧',
                valueSetter: data => {
                  return `${DetailSctionRenderer.NUMBER()(data?.totalExpectedCredit || 0)}${UNIT_STRINGS.CREDIT}`;
                },
              },
            ],
          ]}
        />
        <SectionCard
          cardTitle={
            <div className="flex w-full items-center justify-between">
              <span className="font-pretendardSemiBold text-b18sb">
                가입 회원 정보
              </span>
              <ExcelDownloadButton
                downloadExcelAPI={getCustomerByPartnerLinkIdExcelAPI}
                fileName={`${partnerCode}_기업_회원_정보`}
                searchParams={{ signupSourceId: partnerLinkId }}
                buttonSize="small"
                disabled={!customerListData?.rows?.length}
              />
            </div>
          }
          className="bg-inherit p-0 !shadow-none"
        >
          <Form {...form}>
            <form
              className="mb-4 grid grid-cols-[1fr_auto_1fr_auto] gap-4"
              onSubmit={form.handleSubmit(handleSubmit)}
            >
              <FormField
                control={form.control}
                name="createdAt"
                render={({ field, fieldState: { error } }) => (
                  <FormContent
                    label="가입 기간"
                    labelClassName="w-[144px] !text-b16m text-gray7 translate-y-2 font-pretendardMedium"
                    error={error}
                  >
                    <DateRangePicker
                      {...field}
                      onChange={e => {
                        field.onChange(e);

                        form.setValue(
                          'createdAtFrom',
                          e?.from
                            ? `${dayjs(e?.from).format('YYYY-MM-DD')} 00:00:00`
                            : '',
                        );
                        form.setValue(
                          'createdAtTo',
                          e?.to
                            ? `${dayjs(e?.to).format('YYYY-MM-DD')} 23:59:59`
                            : '',
                        );
                      }}
                    />
                  </FormContent>
                )}
              />
              <div className="h-full w-[1px] bg-gray1" />
              <FormField
                control={form.control}
                name="nickLike"
                render={({ field, fieldState: { error } }) => (
                  <FormContent
                    label="닉네임"
                    labelClassName="w-[144px] !text-b16m text-gray7 translate-y-2 font-pretendardMedium"
                    error={error}
                  >
                    <Input
                      {...field}
                      onClear={() => field.onChange('')}
                      placeholder="닉네임을 입력해주세요"
                      isEnterSubmit
                    />
                  </FormContent>
                )}
              />
              <Button
                type="submit"
                className="flex-shrink-0"
                buttonColor="secondary"
              >
                {BUTTON_CONST.search}
              </Button>
            </form>
          </Form>
          <Table
            ref={gridRef}
            rowData={customerListData?.rows || []}
            isPaginationDataMapping
            columnDefs={PartnerParticipantListColumn}
            total={customerListData?.count || 0}
            page={1}
            pageSize={10}
            handlePaginationClick={() => {}}
          />
        </SectionCard>
      </div>
    </Dialog>
  );
};

export default PartnerParticipantsDetail;
